import "twin.macro";

import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import tw from "twin.macro";

import { Transition } from "./Transition";
import { SwitchSheetsTitlesResult } from "@/sheets/types/switch-sheets-titles.type";
import { SwitchSheetsLeadIconResult } from "@/sheets/types/switch-sheets-lead-icons";
import { SwitchSheetsHeaderComponentsResult } from "@/sheets/types/switch-sheets-header-components.type";
import { useAutoFitText } from "@/hooks/useAutoFitText";

export type SheetDirection = "forward" | "backward";

export type BottomSheetStackApi<V extends { id: string }> = {
  stack: V[];
  /** The current top-of-stack view. `null` when the sheet is closed. */
  current: V | null;
  /**
   * Last non-null view. Stays populated during the sheet's exit animation so
   * the panel still has content to display while it slides out. Use this for
   * rendering.
   */
  visibleView: V | null;
  isOpen: boolean;
  canGoBack: boolean;
  direction: SheetDirection;
  /** Open the sheet with a single view, resetting the stack. */
  open: (view: V) => void;
  /** Push a new view onto the stack (shows chevron back). */
  push: (view: V) => void;
  /** Pop back to the previous view. Closes the sheet if on root. */
  pop: () => void;
  /** Replace the current view without changing the stack depth. */
  replace: (view: V) => void;
  /** Close the sheet and clear the stack. */
  close: () => void;
};

export function useBottomSheetStack<
  V extends { id: string; canGoBack: boolean } = {
    id: string;
    canGoBack: boolean;
  },
>(): BottomSheetStackApi<V> {
  const [stack, setStack] = useState<V[]>([]);
  const [direction, setDirection] = useState<SheetDirection>("forward");
  const [lastVisible, setLastVisible] = useState<V | null>(null);

  const current = stack[stack.length - 1] ?? null;
  const visibleView = current ?? lastVisible;

  useEffect(() => {
    if (current) setLastVisible(current);
  }, [current]);

  return useMemo(
    () => ({
      stack,
      current,
      visibleView,
      isOpen: stack.length > 0,
      canGoBack: stack.length > 1 && current?.canGoBack,
      direction,
      open: (view: V) => {
        setDirection("forward");
        setStack([view]);
      },
      push: (view: V) => {
        setDirection("forward");
        setStack((s) => [...s, view]);
      },
      pop: () => {
        setDirection("backward");
        setStack((s) => s.slice(0, -1));
      },
      replace: (view: V) => {
        setDirection("forward");
        setStack((s) => [...s.slice(0, -1), view]);
      },
      close: () => {
        setDirection("backward");
        setStack([]);
      },
    }),
    [stack, current, visibleView, direction]
  );
}

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  /** When provided, a chevron back button is rendered in the header. */
  onBack?: () => void;
  title?: SwitchSheetsTitlesResult;
  headerComponent?: SwitchSheetsHeaderComponentsResult;
  /** Leading slot (e.g. an emoji). Ignored when `onBack` is set. */
  leadingIcon?: SwitchSheetsLeadIconResult;
  /** Should change each time the active view changes — drives the slide animation. */
  viewKey?: string | number | null;
  direction?: SheetDirection;
  /** When true, clicking backdrop or pressing Esc closes the sheet. Defaults to false. */
  dismissOnOutsideClick?: boolean;
  children: ReactNode;
};

const slideVariants = {
  enter: (direction: SheetDirection) => ({
    x: direction === "forward" ? 40 : -40,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: SheetDirection) => ({
    x: direction === "forward" ? -40 : 40,
    opacity: 0,
  }),
};

const AutoFitTitle = ({
  leadingIcon,
  title,
}: {
  leadingIcon: ReactNode;
  title: ReactNode;
}) => {
  // We wrap the <p> in a flex-1/min-w-0 div so the hook can measure the
  // actual width allocated by flex layout (otherwise the <p> itself is the
  // flex item and its parent.clientWidth is the full header, not the slot).
  const { ref } = useAutoFitText<HTMLParagraphElement>({
    maxFontSize: 20,
    minFontSize: 14,
    deps: [title, leadingIcon],
  });
  return (
    <div tw="flex-1 min-w-0">
      <p ref={ref} tw="text-center font-medium">
        {leadingIcon ?? <span tw="inline-block w-6" />}
        {title}
      </p>
    </div>
  );
};

const Header = ({
  onBack,
  leadingIcon,
  title,
  headerComponent,
}: {
  onBack?: () => void;
  leadingIcon: ReactNode;
  title: ReactNode;
  headerComponent?: SwitchSheetsHeaderComponentsResult;
}) => (
  <div tw="flex w-full shrink-0 items-center text-stone-600 p-4 mb-4 border-b border-gray-200 pb-4 gap-2">
    {/* Left slot — back button (or empty placeholder to keep the title centered) */}
    <div tw="w-8 shrink-0 flex items-center justify-start">
      {onBack && (
        <button
          type="button"
          aria-label="Retour"
          onClick={onBack}
          css={[
            tw`flex items-center justify-center rounded-full p-1 -ml-1 hover:bg-gray-100 active:bg-gray-200 transition`,
          ]}
        >
          <ChevronLeft tw="h-8 w-8" />
        </button>
      )}
    </div>

    {/* Title (or custom header) */}
    {headerComponent?.hasHeaderComponent ? (
      headerComponent.headerComponent()
    ) : (
      <AutoFitTitle leadingIcon={leadingIcon} title={title} />
    )}

    {/* Right slot — empty, balances the left slot so the title stays centered */}
    <div tw="w-8 shrink-0" />
  </div>
);
export function BottomSheet({
  open,
  onClose,
  onBack,
  title,
  headerComponent,
  leadingIcon,
  viewKey,
  direction = "forward",
  dismissOnOutsideClick = false,
  children,
}: BottomSheetProps) {
  const dragControls = useDragControls();

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        tw="relative z-10"
        onClose={() => {
          if (dismissOnOutsideClick) onClose();
        }}
      >
        {/* Backdrop */}
        <Transition.Child
          as={"div"}
          aria-hidden="true"
          enter={tw`ease-out duration-300`}
          enterFrom={tw`opacity-0`}
          enterTo={tw`opacity-100`}
          leave={tw`ease-in duration-200`}
          leaveFrom={tw`opacity-100`}
          leaveTo={tw`opacity-0`}
          tw="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
        />

        {/* Panel container */}
        <div tw="fixed inset-0 z-10 flex items-end justify-center">
          <Transition.Child
            as={Dialog.Panel}
            enter={tw`ease-out duration-300`}
            enterFrom={tw`opacity-0 translate-y-full`}
            enterTo={tw`opacity-100 translate-y-0`}
            leave={tw`ease-in duration-200`}
            leaveFrom={tw`opacity-100 translate-y-0`}
            leaveTo={tw`opacity-0 translate-y-full`}
            tw="z-10 w-[95%] max-w-[775px] max-h-[90dvh] flex flex-col"
          >
            <motion.div
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              // Élasticité 1 vers le bas — le sheet suit le doigt 1:1, sans
              // résistance, jusqu'à atteindre les seuils de fermeture.
              dragElastic={{ top: 0, bottom: 1 }}
              // Rebond plus mou (stiffness baissée, damping baissé) pour un
              // retour plus naturel quand le drag est relâché en deçà des
              // seuils — donne aussi une sensation plus "glissable".
              dragTransition={{ bounceStiffness: 300, bounceDamping: 28 }}
              onDragEnd={(_, info) => {
                // Seuils baissés : ferme dès 70px de drag OU 250 px/s de
                // vélocité (avant : 120px / 500 px/s). Beaucoup plus réactif
                // sur mobile, surtout pour les gestes courts.
                if (info.offset.y > 70 || info.velocity.y > 250) {
                  onClose();
                }
              }}
              tw="bg-white text-black rounded-t-2xl overflow-hidden shadow-xl flex flex-col h-full w-full"
            >
              {/* Zone de prise — englobe le drag handle ET le header pour
                  que l'utilisateur puisse glisser depuis n'importe où dans
                  cette bande pour fermer. `touch-action: none` désactive le
                  scroll vertical natif de la zone (sinon il volerait le geste
                  au lieu de le passer à framer-motion). Les boutons à l'intérieur
                  du Header (back, close, etc.) restent cliquables : un tap
                  court appelle `dragControls.start` mais sans mouvement
                  suffisant le drag ne s'active pas et le `click` passe. */}
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{ touchAction: "none" }}
                tw="shrink-0 select-none"
              >
                {/* Drag handle visuel — c'est lui seul qui porte le curseur
                    grab/grabbing pour ne pas désorienter l'utilisateur sur
                    les boutons du header. */}
                <div
                  role="button"
                  aria-label="Faire glisser pour fermer"
                  tw="flex justify-center items-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
                >
                  <div tw="h-1.5 w-10 rounded-full bg-gray-300" />
                </div>

                <Header
                  onBack={onBack}
                  leadingIcon={
                    leadingIcon?.hasIcon ? leadingIcon.icon() : undefined
                  }
                  title={title?.hasTitle ? title.title : undefined}
                  headerComponent={headerComponent}
                />
              </div>

              {/* Content — animates between stacked views */}
              <div tw="flex-1 overflow-y-auto">
                <AnimatePresence mode="wait" initial={false} custom={direction}>
                  <motion.div
                    key={viewKey ?? "single"}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    tw="w-full"
                  >
                    {children}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default BottomSheet;
